import { useDisclosure } from '@chakra-ui/react';
import { useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { useRequestNumberBlock } from '../queries/useRequestNumberBlock';

import AGGridMain from 'components/tables/AGGrid/AGGridMain';
import CopyCellRenderer from 'components/tables/AGGrid/CustomCells/CopyCellRenderer';
import NumberRangeCellRenderer from 'components/tables/AGGrid/NumberRangeCellRenderer';
import { Alert, AlertIcon, Text } from 'components/v4';
import PlusIconButton from 'pages/telephony/numbers/new/PlusIconButton';
import EditModal from './EditModal';
import CountryFlag from 'components/icons/CountryFlag';
import { useQueryClient } from '@tanstack/react-query';

export default function AvailableNumberBlocksMiniTable({
    onDrawerClose,
    data,
    queryStatus,
    isRefetching,
    qk,
    refetch,
    countries,
}) {
    const gridRef = useRef();
    const queryClient = useQueryClient();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [selectedRow, setSelectedRow] = useState({});
    const { darkMode } = useSelector((state) => state.settings);
    const { currentCompany, currentCompanyData } = useSelector((state) => {
        return { ...state.navigationLists };
    });
    const isNotApproved = !currentCompanyData?.approved;

    const rowData = useMemo(
        () =>
            data.map((nb) => {
                const country =
                    countries?.find((c) => c.id === nb.countryID) || {};
                return {
                    ...nb,
                    country,
                    countryName: country.name,
                    countryCode: country.alpha2,
                };
            }),
        [data, countries],
    );
    const colDefs = useMemo(
        () => [
            {
                field: 'countryID',
                headerName: 'Country',
                valueFormatter: ({ data }) => {
                    return data.country?.name;
                },
                hide: !rowData?.every((r) => r.countryID), // NOTE: check with Andy whether hidden if no countryID
                filter: 'agSetColumnFilter',
                filterParams: {
                    values: rowData?.map((r) => r?.countryName),
                },
                valueGetter: 'data.country?.name',
                cellRenderer: (params) => {
                    const countryCode = params.data?.country?.alpha2;
                    const title = params?.data?.country?.name;
                    return (
                        <CountryFlag
                            countryCode={countryCode}
                            title={title}
                            style={{
                                width: '24px',
                                height: '16px',
                                backgroundColor: params?.data?.countryID
                                    ? null
                                    : '#fff',
                            }}
                        />
                    );
                },
            },
            {
                field: 'countryName',
                hide: true,
            },
            {
                field: 'first',
                headerName: 'Number Range',
                cellRenderer: NumberRangeCellRenderer,
                cellStyle: () => {
                    return { width: '100%' };
                },
            },
            {
                field: 'blockSize',
                headerName: 'Block Size',
                valueFormatter: (params) => {
                    return params.value;
                },
                cellRendererParams: {
                    suppressPadding: false,
                    innerRenderer: CopyCellRenderer,
                },
                cellStyle: () => {
                    return { width: '100%' };
                },
            },
            {
                maxWidth: 140,
                width: 70,
                filter: false,
                suppressMovable: true,
                suppressSizeToFit: true,
                sortable: false,
                cellStyle: () => ({
                    display: 'flex',
                    alignItems: 'center',
                }),
                lockPosition: 'right',
                headerName: 'Action',
                cellRenderer: (params) => {
                    const currentRow = params.data;
                    const currentIndex = data.findIndex(
                        (r) => r?.id === currentRow?.id,
                    );
                    return (
                        <PlusIconButton
                            {...{
                                currentRow,
                                currentIndex,
                                darkMode,
                                onOpen,
                                setSelectedRow,
                            }}
                        />
                    );
                },
            },
        ],
        [onOpen, darkMode, data, rowData],
    );

    const [tableData] = useState({
        columns: colDefs,
        data: rowData,
    });
    // const getQuickFilterText = (params) => {
    //     return `${params.data.countryName}`;
    // };
    const isLoading = queryStatus === 'loading' || isRefetching;

    const requesBlockOpts = {
        id: selectedRow?.id,
        currentCompany,
        qk,
    };

    const { mutate, status } = useRequestNumberBlock(requesBlockOpts);

    const handleEditOnClick = () => {
        mutate(selectedRow, {
            onSuccess: () => {
                toast.success('Requesting Block Sent');
                setSelectedRow({});
                onClose();
                onDrawerClose();
                refetch(); // availableNumberBlocks
                queryClient.invalidateQueries({
                    queryKey: ['numbers'],
                });
            },
        });
    };
    const handleSubmit = (e) => {
        e.preventDefault();
        handleEditOnClick();
    };
    const editModalProps = {
        isOpen,
        onClose,
        status,
        selectedRow,
        handleSubmit,
    };

    const content =
        !isLoading && isNotApproved ? (
            <Alert status="info">
                <AlertIcon />

                <Text>
                    Assignment of public number blocks is only available to
                    approved companies.{' '}
                </Text>
            </Alert>
        ) : (
            <>
                <AGGridMain
                    gridRef={gridRef}
                    isLoading={isLoading}
                    refetch={refetch}
                    titleName="Available Number Blocks"
                    overlayNoRowsTemplate="No Numbers Found"
                    tableData={tableData}
                    suppressMenuHide={false}
                    // getQuickFilterText={getQuickFilterText}
                />
                {selectedRow && <EditModal {...editModalProps} />}
            </>
        );

    return content;
}
